<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="smkexchangerate-main">
    <Menus :menu="menus" @menuClicked="handleMenuClick" />
    <div class="right-container">
      <TableBox
        title="All Currencies"
        :tableData="currencyData"
        :selectedRow.sync="selectedCurrency"
        :loading="tableLoading"
        :hideColumns="['Currency Rate', 'Exchange Date', 'Factor', 'combined']"
      />
    </div>
    <PopupEntry
      v-if="popupEntryShow"
      :show.sync="popupEntryShow"
      :title="popUpTitle"
      :viewMode="viewMode"
      :successMessages="successMessages"
      @save="saveYearClicked"
    >
      <div class="popup-entry-container">
        <div class="popup-top-section">
          <Dropdown
            :items.sync="currencyData"
            itemKey="combined"
            :selectedObject.sync="selectedDropdownCurrency"
            placeholder="Select Currency"
            :isDisabled="popUpAction === 'E' || popUpAction === 'V'"
            @selected="onCurrencyDropdownSelect"
          />
          <Dropdown
            :items.sync="yearData"
            itemKey="Year"
            :selectedObject.sync="selectedYear"
            placeholder="Select Year"
            @selected="onYearSelect"
            :isDisabled="selectedDropdownCurrency === null"
          />
        </div>
        <div class="smkexchangerate-tablebox">
          <div class="smkexchangerate-table-buttons">
            <button v-if="showDelete" @click="deleteYearClicked" :disabled="popUpTitle !== 'Edit SMK Exchange Rate'">
              <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png" alt="Delete Year" />
              Delete Year
            </button>
          </div>
          <div class="smkexchangerate-table-main-content">
            <TableBox
              title="SMK Exchange Rate"
              :tableData="SMKExchangeRateData"
              :selectedRow.sync="selectedSMKExchangeRate"
              v-if="reloadTable"
              :additionalButtons="viewMode ? [] : [{ text: 'Edit', index: 0, width: 75 }]"
              @buttonClicked="editClicked"
              :maxSelection="0"
              :loading="yearLoading"
            />
          </div>

          <div v-if="showEditSection" class="edit-section">
            <div class="popup-edit-section">
              <button @click="editSaveClicked" :disabled="viewMode"> Edit </button>
              <button @click="cancelEditClicked" :disabled="viewMode"> Cancel </button>
            </div>
            <DateTimePicker :value.sync="editStartDate" :showTime="false" :isDisabled="true" placeholder="From" />
            <DateTimePicker :value.sync="editEndDate" :showTime="false" :isDisabled="true" placeholder="To" />
            <Input
              placeholder="Import"
              :isRequired="true"
              :inputValue.sync="editImport"
              validator="positiveDecimal"
              :errorMessage.sync="errorImportMessage"
            />
            <Input
              placeholder="Export"
              :isRequired="true"
              :inputValue.sync="editExport"
              validator="positiveDecimal"
              :errorMessage.sync="errorExportMessage"
            />
          </div>
        </div>
      </div>
    </PopupEntry>
  </div>
</template>

<script>
export default {
  computed: {},
  data: function () {
    return {
      //Company Info
      localcur: null,
      // Summary table
      currencyData: [],
      selectedCurrency: [],
      tableLoading: true,

      //Popup
      viewMode: false,
      popUpTitle: '',
      popUpAction: '',
      popupEntryShow: false,
      successMessages: [],

      //Popup data
      yearData: null,
      selectedYear: null,
      selectedDropdownCurrency: null, //For the currency dropdown

      //Popup table
      SMKExchangeRateData: [],
      selectedSMKExchangeRate: [],
      reloadTable: false,
      yearLoading: true,
      showDelete: false,

      //Edit row
      isEditing: false,
      showEditSection: false,
      editStartDate: null,
      editEndDate: null,
      editImport: '',
      errorImportMessage: '',
      editExport: '',
      errorExportMessage: '',

      //Menu
      menus: [
        {
          title: 'Create SMK Exchange Rate',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          key: 'MnuNew',
          visible: true,
          enabled: true,
        },
        {
          title: 'Edit SMK Exchange Rate',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: true,
        },
        {
          title: 'View SMK Exchange Rate',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: '',
          visible: true,
          enabled: true,
        },
      ],
    };
  },
  methods: {
    handleMenuClick: async function (menu) {
      this.showEditSection = false;
      switch (menu.title) {
        case 'Create SMK Exchange Rate': {
          this.viewMode = true;
          this.popUpTitle = 'Create SMK Exchange Rate';
          this.popUpAction = 'N';
          this.popupEntryShow = true;
          await this.fillPopupDetails();
          break;
        }
        case 'View SMK Exchange Rate': {
          if (this.selectedCurrency.length == 0) {
            this.$showAlert('error', 'No Currency Selected', 'Please select a record to view');
            return;
          }
          this.viewMode = true;
          this.popUpTitle = 'View SMK Exchange Rate';
          this.popUpAction = 'V';
          this.popupEntryShow = true;
          await this.fillPopupDetails();
          break;
        }
        case 'Edit SMK Exchange Rate': {
          if (this.selectedCurrency.length == 0) {
            this.$showAlert('error', 'No Currency Selected', 'Please select a record to view');
            return;
          }
          this.viewMode = false;
          this.popUpTitle = 'Edit SMK Exchange Rate';
          this.popUpAction = 'E';
          this.popupEntryShow = true;
          await this.fillPopupDetails();
          break;
        }
      }
    },
    fillPopupDetails() {
      this.selectedDropdownCurrency = this.selectedCurrency[0] || null;
      if (this.selectedDropdownCurrency) this.onCurrencyDropdownSelect();
    },
    async onCurrencyDropdownSelect() {
      this.selectedYear = null;

      await this.loadTable();
    },
    async loadTable() {
      this.SMKExchangeRateData = null;
      this.reloadTable = false;
      if(!this.selectedDropdownCurrency['Currency Code']) return;
      this.$fullscreenLoading('Getting Years');
      const getYears = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'SMKEXCHANGERATEYEARS',
        module: this.selectedDropdownCurrency['Currency Code'],
      });
      this.$fullscreenLoading();

      if (this.popUpAction === 'N') {
        const creatableYears = [];
        const latestYear = getYears[0].Year;
        const currYear = new Date().getFullYear();
        const startingYear = Number(latestYear) === currYear ? currYear + 1 : currYear;
        const endingYear = currYear + 3;

        for (let year = endingYear; year >= startingYear; year--) {
          const jsonBody = {
            Year: year.toString(),
            ExCode: this.selectedDropdownCurrency['Currency Code'],
            ExDesc: this.selectedDropdownCurrency['Description'],
            ExFactor: this.selectedDropdownCurrency['Factor'],
          };
          creatableYears.push(jsonBody);
        }
        this.yearData = creatableYears;
      } else {
        this.yearData = getYears.map((yearValue) => ({
          ...yearValue,
          ExCode: this.selectedDropdownCurrency['Currency Code'],
          ExDesc: this.selectedDropdownCurrency['Description'],
          ExFactor: this.selectedDropdownCurrency['Factor'],
        }));

        this.selectedYear = this.selectedYear ?? this.yearData[0];
        this.onYearSelect();
      }
    },
    async onYearSelect() {
      this.showEditSection = false;
      if (this.selectedYear === null) return;
      if (this.popUpAction === 'N') {
        this.viewMode = false;
      }
      const jsonBody = {
        year: this.selectedYear.Year,
        excode: this.selectedDropdownCurrency['Currency Code'],
      };

      const data =
        this.popUpAction === 'N' ? null : await this.$apiCallV2('/SMKExchangeRate/GetSMKExchangeRateYear', jsonBody);

      const newYear = new Date(this.selectedYear.Year, 0, 1);

      // Monday = 0, Tuesday = 1, ..., Sunday = 6
      const weekday1 = (newYear.getDay() + 6) % 7;

      const monday = new Date(newYear);
      let noOfRows;
      if (weekday1 < 3) {
        monday.setDate(newYear.getDate() - weekday1);
        noOfRows = 53;
      } else {
        monday.setDate(newYear.getDate() - weekday1 + 7);
        noOfRows = 52;
      }

      let weekNum = 1;
      const createNewYearDefault = this.selectedDropdownCurrency['Currency Code'] === this.localcur ? 1 : '';
      const selectedYear = Number(this.selectedYear.Year);
      const today = new Date();
      const SMKTableData = [];
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < noOfRows; i++) {
        // Preserve the current-year restriction.
        if (selectedYear === today.getFullYear() && monday > today) {
          break;
        }

        const sunday = new Date(monday);
        sunday.setDate(sunday.getDate() + 6);

        const jsonObj = {
          Week: weekNum,
          From: monday.formatString('yyyy-MM-dd'),
          To: sunday.formatString('yyyy-MM-dd'),
          Import: (this.popUpAction === 'N' ? createNewYearDefault : data[0][`Wk${weekNum}Imp`]) ?? '',
          Export: (this.popUpAction === 'N' ? createNewYearDefault : data[0][`Wk${weekNum}Exp`]) ?? '',
        };

        weekNum++;

        monday.setDate(monday.getDate() + 7);

        if (this.popUpAction === 'N') {
          jsonObj['Next Week Import'] = createNewYearDefault;
          jsonObj['Next Week Export'] = createNewYearDefault;
        } else {
          jsonObj['Next Week Import'] = data[0][`Wk${weekNum}Imp`] || '';
          jsonObj['Next Week Export'] = data[0][`Wk${weekNum}Exp`] || '';
        }

        SMKTableData.push(jsonObj);
      }

      this.SMKExchangeRateData = SMKTableData;
      this.reloadTableFunc();
    },
    reloadTableFunc() {
      this.reloadTable = false;
      this.yearLoading = true;
      this.$nextTick(() => {
        this.yearLoading = false;
        this.reloadTable = true;
      });
    },
    async deleteYearClicked() {
      const deleting = await this.$showAlert(
        'warning',
        'Delete Year',
        `Are you sure you want to delete for this year?`,
        [
          { text: 'Delete', textColor: 'white', color: 'red' },
          { text: 'Cancel', textColor: 'black', color: 'lightgray' },
        ],
      );

      if (deleting === 'Delete') {
        const jsonBody = {
          actionflag: 'D',
          excode: this.selectedYear['ExCode'],
          year: this.selectedYear.Year,
        };

        await this.$apiCallV2('/SMKExchangeRate/SaveSMKExchangeRate', jsonBody);
        this.yearData = this.yearData.filter((obj) => obj.Year !== this.selectedYear.Year);
        this.selectedYear = this.yearData[0];
        this.onYearSelect();
      }
    },
    editClicked(_, rowClicked) {
      this.isEditing = true;
      this.showEditSection = true;
      this.editStartDate = new Date(rowClicked['From']);
      this.editEndDate = new Date(rowClicked['To']);
      this.editImport = rowClicked['Import']?.toString() ?? '';
      this.editExport = rowClicked['Export']?.toString() ?? '';
    },
    cancelEditClicked() {
      this.isEditing = false;
      this.showEditSection = false;
      this.editStartDate = null;
      this.editEndDate = null;
      this.editImport = '';
      this.editExport = '';
    },
    editSaveClicked() {
      const importCheck = this.editImport.trim() !== '' && Number.isFinite(Number(this.editImport));
      const exportCheck = this.editExport.trim() !== '' && Number.isFinite(Number(this.editExport));
      let error = false;

      if (!importCheck) {
        error = true;
        this.errorImportMessage = 'Must be a valid number';
      }
      if (!exportCheck) {
        error = true;
        this.errorExportMessage = 'Must be a valid number';
      }

      if (error) return;

      // const selectedWeek = this.SMKExchangeRateData.getSelected(this.editStartDate.formatString('yyyy-MM-dd'), 'From');
      const selectedWeekIndex = this.SMKExchangeRateData.findIndex(
        (week) => this.editStartDate.formatString('yyyy-MM-dd') === week.From,
      );
      const selectedWeek = this.SMKExchangeRateData[selectedWeekIndex];
      selectedWeek.Import = this.editImport;
      selectedWeek.Export = this.editExport;
      if (selectedWeekIndex != 0) {
        const previousWeek = this.SMKExchangeRateData[selectedWeekIndex - 1];
        previousWeek['Next Week Import'] = this.editImport;
        previousWeek['Next Week Export'] = this.editExport;
      }
      this.reloadTableFunc();
    },
    async saveYearClicked() {
      //Use SMKExchangeRateData
      const jsonBody = {};
      jsonBody.year = this.selectedYear.Year;
      jsonBody.excode = this.selectedYear['ExCode'];

      this.SMKExchangeRateData.forEach((row) => {
        jsonBody[`wk${row.Week}imp`] = row.Import || null;
        jsonBody[`wk${row.Week}exp`] = row.Export || null;
      });

      if (this.popUpTitle === 'Create SMK Exchange Rate') {
        // For now N or E for saving
        jsonBody.actionflag = 'N';
        jsonBody.exdesc = this.selectedDropdownCurrency['Description'];
        jsonBody.exfactor = this.selectedDropdownCurrency['Factor'];
      } else {
        jsonBody.actionflag = 'E';
      }

      await this.$apiCallV2('/SMKExchangeRate/SaveSMKExchangeRate', jsonBody);
      this.viewMode = true;
      this.popUpTitle = 'View SMK Exchange Rate';
      this.popUpAction = 'V';
      this.loadTable();
      this.reloadTableFunc();
    },
    async sasControlFunction() {
      this.$fullscreenLoading('Checking Security Access');
      const sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=MASTER&page=SMK Exchange Rate`);

      sasControl.forEach((k) => {
        //key in menu same as control value from api
        const selectedMenu = this.menus.find((m) => m['key'] == k['Control']);
        if (selectedMenu) {
          selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
        }
      });
      const deleteSASControl = sasControl.find((value) => value.Control === 'MnuDelete');
      if (deleteSASControl) {
        this.showDelete = deleteSASControl['PropertiesValue'] == 'True';
      }

      this.$fullscreenLoading('');
    },
  },
  async mounted() {
    await this.sasControlFunction();
    const cocode = (await this.$apiCallV2('/Login/GetDetails'))['CoCode'];
    this.$fullscreenLoading('Getting Details');
    this.localcur = (
      await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'COMPANY',
        module: 'MASTER',
        recordid: cocode,
      })
    )['Company']['CompanyLocalCur'];
    this.currencyData = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' });
    this.$fullscreenLoading();
    this.currencyData = this.currencyData.map((value) => ({
      ...value,
      combined: `${value['Currency Code']} - ${value['Description']}`,
    }));
    this.tableLoading = false;
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.smkexchangerate-main {
  padding: 5px 45px;
  font-size: 12px;
  display: flex;
  height: 100%;
  > .right-container {
    background-color: white;
    padding: 5px;
    border-radius: 5px;
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
  }

  .popup-entry-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    > .popup-top-section {
      display: flex;
      flex-direction: column;
      padding-bottom: 5px;
    }

    > .smkexchangerate-tablebox {
      height: 100%;
      display: flex;
      flex-direction: row;
      min-height: 0;

      > .smkexchangerate-table-buttons {
        display: flex;
        flex-direction: column;
        margin-top: 40px;

        > button {
          text-align: left;
          width: 120px;

          &:not(:first-child) {
            margin-top: 5px;
          }

          > img {
            height: 20px;
            width: 20px;
          }
        }
      }
      > .smkexchangerate-table-main-content {
        margin-left: 5px;
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      > .edit-section {
        height: 100%;
        width: 30%;
        padding-left: 5px;

        > .popup-edit-section {
          flex-direction: row;
          gap: 5px;
          justify-content: flex-end;
          display: flex;
          padding-right: 10px;
          margin-bottom: 8px;
        }
      }
    }
  }
}
</style>
